import React, { useEffect, useRef } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { Panel } from 'primereact/panel';
import { InputTextarea } from 'primereact/inputtextarea';
import WheelFieldLabel from './WheelFieldLabel';
import WheelOptionTiles from './WheelOptionTiles';
import {
  WHEEL_TYPE_OPTIONS,
  WHEEL_RESTORATION_METHOD_OPTIONS,
  WHEEL_CLEAR_COAT_OPTIONS,
  WHEEL_PRECISION_METHOD_OPTIONS,
  WHEEL_YES_NO_OPTIONS,
  WHEEL_GOOD_BAD_OPTIONS,
  WHEEL_POSITION_CHECK_OPTIONS,
  WHEEL_RESTORATION_METHOD_MAX,
} from '../../constants/WheelInspectionConstants';
import {
  getWheelInspectionFieldPath,
  syncSafetyPositionsFromPreRestoration,
} from '../../utils/wheelInspectionUtil';

const thClass =
  'text-center font-semibold p-3 border-1 border-300 white-space-nowrap';
const thClassWrap =
  'text-center font-semibold p-3 border-1 border-300';
const tdClass = 'p-3 border-1 border-300 align-top';
const criteriaColClass = 'wheel-inspection-criteria-col';
const categoryColClass = 'wheel-inspection-category-col';

const SAFETY_DIAGNOSIS_ROWS = [
  {
    key: 'precisionMethods',
    title: '정밀 검사 방법',
    type: 'checkbox',
    options: WHEEL_PRECISION_METHOD_OPTIONS,
    path: 'safety.precisionMethods',
  },
  {
    key: 'crack',
    title: '크렉',
    sub: '휠 전체 (기준: 2cm 이하)',
    type: 'radio',
    options: WHEEL_YES_NO_OPTIONS,
    path: 'safety.crack',
    positionsPath: 'safety.crackPositions',
  },
  {
    key: 'hairline',
    title: '실금',
    sub: '표면 실금 유무',
    type: 'radio',
    options: WHEEL_YES_NO_OPTIONS,
    path: 'safety.hairline',
    positionsPath: 'safety.hairlinePositions',
  },
  {
    key: 'paintState',
    title: '도막 상태',
    sub: '도장·코팅 전반',
    type: 'radio',
    options: WHEEL_GOOD_BAD_OPTIONS,
    path: 'safety.paintState',
    positionsPath: 'safety.paintStatePositions',
  },
  {
    key: 'roadForce',
    title: '컷팅 부분',
    sub: '휠 다이아몬드 컷팅 부위',
    type: 'radio',
    options: WHEEL_GOOD_BAD_OPTIONS,
    path: 'safety.roadForce',
    positionsPath: 'safety.roadForcePositions',
  },
  {
    key: 'scratch',
    title: '스크래치',
    sub: '표면 스크래치',
    type: 'radio',
    options: WHEEL_YES_NO_OPTIONS,
    path: 'safety.scratch',
    positionsPath: 'safety.scratchPositions',
  },
];

const WheelSafetyInspectionPanel = ({
  control,
  setValue,
  getValues,
  readOnly = false,
}) => {
  const prevPositionsRef = useRef(undefined);
  const selectedPositions =
    useWatch({
      control,
      name: getWheelInspectionFieldPath('preRestoration.positions'),
    }) || [];

  useEffect(() => {
    if (readOnly || !setValue || !getValues) return;

    const prev = prevPositionsRef.current;
    syncSafetyPositionsFromPreRestoration(selectedPositions, prev || [], {
      setValue,
      getValues,
      isInitial: prev === undefined,
    });
    prevPositionsRef.current = selectedPositions;
  }, [selectedPositions, setValue, getValues, readOnly]);

  return (
    <Panel header="휠 복원전 검사 항목 (기초진단)" className="shadow-2">
      <div className="grid">
        <div className="col-12 mb-3">
          <WheelFieldLabel>휠 종류</WheelFieldLabel>
          <Controller
            control={control}
            name={getWheelInspectionFieldPath('safety.wheelTypes')}
            defaultValue={[]}
            render={({ field }) => (
              <WheelOptionTiles
                type="checkbox"
                options={WHEEL_TYPE_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                disabled={readOnly}
              />
            )}
          />
        </div>

        <div className="col-12 mb-3">
          <WheelFieldLabel>
            복원 방법 (최대 {WHEEL_RESTORATION_METHOD_MAX}개)
          </WheelFieldLabel>
          <Controller
            control={control}
            name={getWheelInspectionFieldPath('safety.restorationMethods')}
            defaultValue={[]}
            render={({ field }) => (
              <WheelOptionTiles
                type="checkbox"
                options={WHEEL_RESTORATION_METHOD_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                max={WHEEL_RESTORATION_METHOD_MAX}
                disabled={readOnly}
              />
            )}
          />
        </div>

        <div className="col-12 mb-3">
          <WheelFieldLabel>크리어 코트</WheelFieldLabel>
          <Controller
            control={control}
            name={getWheelInspectionFieldPath('safety.clearCoatType')}
            defaultValue=""
            render={({ field }) => (
              <WheelOptionTiles
                type="radio"
                options={WHEEL_CLEAR_COAT_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                disabled={readOnly}
                tileClassName="wheel-option-tile--wide"
              />
            )}
          />
        </div>

        <div className="col-12 mb-3">
          <WheelFieldLabel>검사 항목</WheelFieldLabel>
          <div className="overflow-auto">
            <table className="w-full border-collapse wheel-inspection-table">
              <thead>
                <tr className="bg-primary text-white">
                  <th className={`${thClass} ${categoryColClass}`} style={{ width: '9rem' }}>
                    구분
                  </th>
                  <th className={`${thClassWrap} ${criteriaColClass}`}>
                    세부 검사항목 및 기준
                  </th>
                  <th className={thClass} style={{ minWidth: '22rem' }}>
                    결과
                  </th>
                </tr>
              </thead>
              <tbody>
                {SAFETY_DIAGNOSIS_ROWS.map((row) => (
                  <tr key={row.key}>
                    <th className={`${thClass} bg-gray-100 text-color ${categoryColClass}`}>
                      {row.title}
                    </th>
                    <td className={`${tdClass} ${criteriaColClass}`}>
                      {row.sub && (
                        <span className="wheel-inspection-criteria">{row.sub}</span>
                      )}
                    </td>
                    <td className={tdClass}>
                      <div
                        className={
                          row.positionsPath
                            ? 'wheel-inspection-result-split'
                            : undefined
                        }
                      >
                        {row.positionsPath && (
                          <div className="wheel-inspection-result-split__positions">
                            <span className="wheel-inspection-result-split__label">
                              위치
                            </span>
                            <Controller
                              control={control}
                              name={getWheelInspectionFieldPath(row.positionsPath)}
                              defaultValue={[]}
                              render={({ field }) => (
                                <WheelOptionTiles
                                  type="checkbox"
                                  options={WHEEL_POSITION_CHECK_OPTIONS}
                                  value={field.value}
                                  onChange={field.onChange}
                                  disabled={readOnly}
                                  layout="grid2x2"
                                  tileClassName="wheel-option-tile--position"
                                />
                              )}
                            />
                          </div>
                        )}
                        <div className="wheel-inspection-result-split__value">
                          <Controller
                            control={control}
                            name={getWheelInspectionFieldPath(row.path)}
                            defaultValue={row.type === 'checkbox' ? [] : ''}
                            render={({ field }) => (
                              <WheelOptionTiles
                                type={row.type}
                                options={row.options}
                                value={field.value}
                                onChange={field.onChange}
                                disabled={readOnly}
                                layout={
                                  row.type === 'checkbox' ? 'horizontal' : 'vertical'
                                }
                                tileClassName="wheel-option-tile--result"
                              />
                            )}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-12 mb-2">
          <WheelFieldLabel>특이사항</WheelFieldLabel>
          <Controller
            control={control}
            name={getWheelInspectionFieldPath('safety.specialNote')}
            defaultValue=""
            render={({ field }) => (
              <InputTextarea
                id={field.name}
                {...field}
                className="w-full"
                rows={4}
                autoResize
                readOnly={readOnly}
                placeholder="특이사항을 입력하세요."
              />
            )}
          />
        </div>
      </div>
    </Panel>
  );
};

export default WheelSafetyInspectionPanel;
